"use client";

import { c as _c } from "react/compiler-runtime";
import { IconXboxX } from "@tabler/icons-react";
import { AnimatePresence, motion } from "motion/react";
import { NotificationCard } from "@/components/notifications/notification-card";
import { NotificationEmptyState } from "@/components/notifications/notification-empty-state";
import { NotificationListSkeleton } from "@/components/notifications/notification-list-skeleton";
import type { NotificationData } from "@domainstack/types";
import { Button } from "@domainstack/ui/button";
import { ScrollArea } from "@domainstack/ui/scroll-area";
import { Spinner } from "@domainstack/ui/spinner";
interface NotificationListProps {
  notifications: NotificationData[];
  isLoading: boolean;
  isError: boolean;
  view: "inbox" | "archive";
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  isFetchNextPageError?: boolean;
  onRetryNextPage?: () => void;
  loadMoreRef?: React.RefObject<HTMLDivElement | null>;
  scrollAreaRef?: React.RefObject<HTMLDivElement | null>;
  onNotificationClick?: (notification: NotificationData) => void;
  onClosePopover?: () => void;
}
export function NotificationList(t0) {
  const $ = _c(35);
  const {
    notifications,
    isLoading,
    isError,
    view,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    onRetryNextPage,
    loadMoreRef,
    scrollAreaRef,
    onNotificationClick,
    onClosePopover
  } = t0;
  const t1 = !isLoading && !isError && notifications.length > 0 ? "divide-y" : undefined;
  let t2;
  if ($[0] !== isError || $[1] !== isLoading || $[2] !== notifications || $[3] !== onClosePopover || $[4] !== view) {
    t2 = isLoading ? <NotificationListSkeleton /> : isError ? <div role="alert" className="flex flex-col items-center justify-center gap-1.5 py-12 text-sm"><IconXboxX className="size-4" />Failed to load notifications</div> : notifications.length === 0 ? <NotificationEmptyState variant={view} onClosePopover={onClosePopover} /> : null;
    $[0] = isError;
    $[1] = isLoading;
    $[2] = notifications;
    $[3] = onClosePopover;
    $[4] = view;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== isError || $[7] !== isLoading || $[8] !== notifications || $[9] !== onNotificationClick) {
    t3 = !isLoading && !isError && notifications.map(notification => <motion.div key={notification.id} layout="position" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: "easeInOut"
    }}><NotificationCard notification={notification} onClick={() => onNotificationClick?.(notification)} /></motion.div>);
    $[6] = isError;
    $[7] = isLoading;
    $[8] = notifications;
    $[9] = onNotificationClick;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t3) {
    t4 = <AnimatePresence mode="popLayout" initial={false}>{t3}</AnimatePresence>;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== hasNextPage || $[14] !== isError || $[15] !== isFetchNextPageError || $[16] !== isFetchingNextPage || $[17] !== isLoading || $[18] !== loadMoreRef) {
    t5 = !isLoading && !isError && hasNextPage && !isFetchNextPageError && <div ref={loadMoreRef} className="flex justify-center py-4">{isFetchingNextPage && <Spinner className="size-5 text-muted-foreground" />}</div>;
    $[13] = hasNextPage;
    $[14] = isError;
    $[15] = isFetchNextPageError;
    $[16] = isFetchingNextPage;
    $[17] = isLoading;
    $[18] = loadMoreRef;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== isError || $[21] !== isFetchNextPageError || $[22] !== isFetchingNextPage || $[23] !== isLoading || $[24] !== onRetryNextPage) {
    t6 = !isLoading && !isError && isFetchNextPageError && !isFetchingNextPage && <div role="alert" className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">Couldn't load more notifications.<Button variant="ghost" size="sm" onClick={onRetryNextPage}>Retry</Button></div>;
    $[20] = isError;
    $[21] = isFetchNextPageError;
    $[22] = isFetchingNextPage;
    $[23] = isLoading;
    $[24] = onRetryNextPage;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== t1 || $[27] !== t2 || $[28] !== t4 || $[29] !== t5 || $[30] !== t6) {
    t7 = <div className={t1}>{t2}{t4}{t5}{t6}</div>;
    $[26] = t1;
    $[27] = t2;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[32] !== scrollAreaRef || $[33] !== t7) {
    t8 = <ScrollArea scrollRef={scrollAreaRef} className="min-h-0 flex-1 bg-popover/10">{t7}</ScrollArea>;
    $[32] = scrollAreaRef;
    $[33] = t7;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  return t8;
}